import moment from 'moment';
import formatCurrency from 'format-currency';

const httpOptions = { withCredentials: true };

class InvoiceCreateTravelController {

  constructor(toastr, $http, $rootScope, $state, $q, $uibModal, InvoiceService, TravelExpenseService) {
    'ngInject';

    this.toastr = toastr;
    this.$http = $http;
    this.$rootScope = $rootScope;
    this.$state = $state;
    this.$q = $q;
    this.$uibModal = $uibModal;
    this.moment = moment;
    this.invoiceService = InvoiceService;
    this.travelExpenseService = TravelExpenseService;

    this.step = 1;
    this.expenseReports = [];
    this.selectedExpenseReports = [];
    this.categoryPreview = [];
    this.priorCumulatives = {};
    this.loadingReports = false;
    this.loadingPreviewContext = false;
    this.creating = false;
    this.nextInvoiceNumber = null;
    this.companyLogoUri = null;

    this.invoice = {
      customerId: null,
      customerName: '',
      customer: {},
      contractId: null,
      contractName: '',
      contract: {},
      projectId: null,
      projectName: '',
      project: {},
      company: {},
      invoiceDate: moment().toDate(),
      invoicePeriodStart: null,
      invoicePeriodEnd: null,
      invoiceNumberLabel: '',
      certification: '',
      signerName: '',
      signerTitle: '',
      signerEmail: '',
      signerPhone: ''
    };

    this.calculateInvoicePeriod();

    this.$http.get(`${this.$rootScope.apiHost}/api/admin/invoices/invnumber/next`, httpOptions).then((success) => {
      this.nextInvoiceNumber = success.data;
      this.invoice.invoiceNumber = success.data;
      this.invoice.invoiceNumberLabel = success.data + '-TRAVEL';
    });

    this.$http.get(`${this.$rootScope.apiHost}/api/admin/companies/mycompany`, httpOptions).then((success) => {
      this.invoice.company = success.data || {};
      if (this.invoice.company.id) {
        this.companyLogoUri = `${this.$rootScope.apiHost}/api/admin/companies/${this.invoice.company.id}/logo`;
      }
    });

    this.$http.get(`${this.$rootScope.apiHost}/api/users/me`, httpOptions).then((response) => {
      const user = response.data || {};
      this.invoice.signerName = ((user.firstName || '') + ' ' + (user.lastName || '')).trim();
      this.invoice.signerTitle = user.title || '';
      this.invoice.signerEmail = user.userName || user.email || '';
      this.invoice.signerPhone = user.phone || '';
    });

    this.loadAllEligibleReports();
  }

  getDisplayInvoiceNumber() {
    return this.invoice.invoiceNumberLabel
      || ((this.nextInvoiceNumber || this.invoice.invoiceNumber)
        ? (this.nextInvoiceNumber || this.invoice.invoiceNumber) + '-TRAVEL'
        : '');
  }

  parseInvoiceNumberFromLabel(label) {
    const text = String(label || '').trim();
    const match = text.match(/\d+/);
    if (match) {
      return Number(match[0]);
    }
    return this.nextInvoiceNumber || this.invoice.invoiceNumber || 0;
  }

  calculateInvoicePeriod() {
    const start = moment().subtract(1, 'month').startOf('month');
    const end = moment().subtract(1, 'month').endOf('month');
    this.invoice.invoicePeriodStart = start.toDate();
    this.invoice.invoicePeriodEnd = end.toDate();
  }

  loadAllEligibleReports() {
    this.loadingReports = true;
    this.invoiceService.getTravelEligibleReports()
      .then((success) => {
        this.expenseReports = success.data || [];
        this.selectedExpenseReports = [];
        this.clearBillingContext();
        this.categoryPreview = [];
      })
      .catch(() => {
        this.expenseReports = [];
        this.toastr.error('Could not load eligible travel expense reports.', 'Error');
      })
      .finally(() => {
        this.loadingReports = false;
      });
  }

  clearBillingContext() {
    this.invoice.customerId = null;
    this.invoice.customerName = '';
    this.invoice.customer = {};
    this.invoice.contractId = null;
    this.invoice.contractName = '';
    this.invoice.contract = {};
    this.invoice.projectId = null;
    this.invoice.projectName = '';
    this.invoice.project = {};
    this.invoice.certification = '';
    this.priorCumulatives = {};
  }

  applyBillingContextFromReport(report) {
    this.invoice.customerId = report.customerId || null;
    this.invoice.customerName = report.customerName || '';
    this.invoice.contractId = report.contractId || null;
    this.invoice.contractName = report.contractName || '';
    this.invoice.projectId = report.projectId || null;
    this.invoice.projectName = report.projectName || '';
  }

  loadPreviewContext() {
    if (!this.invoice.customerId || !this.invoice.contractId || !this.invoice.projectId) {
      return this.$q.resolve();
    }

    this.loadingPreviewContext = true;
    const api = this.$rootScope.apiHost;

    return this.$q.all([
      this.$http.get(`${api}/api/admin/customers/${this.invoice.customerId}`, httpOptions),
      this.$http.get(`${api}/api/admin/contracts/${this.invoice.contractId}`, httpOptions),
      this.$http.get(`${api}/api/admin/projects/${this.invoice.projectId}`, httpOptions)
    ]).then((results) => {
      this.invoice.customer = results[0].data || { id: this.invoice.customerId, name: this.invoice.customerName };
      this.invoice.contract = results[1].data || { id: this.invoice.contractId, contractName: this.invoice.contractName };
      this.invoice.project = results[2].data || { id: this.invoice.projectId, name: this.invoice.projectName };
      this.invoice.certification = (this.invoice.contract && this.invoice.contract.invoiceStatement) || this.invoice.certification || '';
    }).catch(() => {
      this.invoice.customer = this.invoice.customer.id
        ? this.invoice.customer
        : { id: this.invoice.customerId, name: this.invoice.customerName };
      this.invoice.contract = this.invoice.contract.id
        ? this.invoice.contract
        : { id: this.invoice.contractId, contractName: this.invoice.contractName };
      this.invoice.project = this.invoice.project.id
        ? this.invoice.project
        : { id: this.invoice.projectId, name: this.invoice.projectName };
      this.toastr.warning('Some invoice preview details could not be loaded.', 'Warning');
    }).finally(() => {
      this.loadingPreviewContext = false;
    });
  }

  loadCumulativesForSelectedProject() {
    if (!this.invoice.projectId) {
      this.priorCumulatives = {};
      this.refreshCategoryPreview();
      return;
    }

    this.invoiceService.getTravelCategoryCumulatives(this.invoice.projectId)
      .then((success) => {
        this.priorCumulatives = {};
        (success.data || []).forEach((row) => {
          this.priorCumulatives[row.category] = row.priorCumulative || 0;
        });
        this.refreshCategoryPreview();
      })
      .catch(() => {
        this.priorCumulatives = {};
        this.refreshCategoryPreview();
      });
  }

  toggleExpenseReport(expenseReport) {
    const index = this.selectedExpenseReports.findIndex((report) => report.id === expenseReport.id);

    if (index >= 0) {
      this.selectedExpenseReports.splice(index, 1);
      if (!this.selectedExpenseReports.length) {
        this.clearBillingContext();
        this.categoryPreview = [];
        this.priorCumulatives = {};
      } else {
        this.applyBillingContextFromReport(this.selectedExpenseReports[0]);
        this.refreshCategoryPreview();
      }
      return;
    }

    if (this.selectedExpenseReports.length
        && this.invoice.projectId
        && expenseReport.projectId !== this.invoice.projectId) {
      this.toastr.warning(
        'All selected expense reports must belong to the same project. Clear the current selection or choose another report for '
          + (this.invoice.projectName || 'this project') + '.',
        'Different Project'
      );
      return;
    }

    this.selectedExpenseReports.push(expenseReport);

    if (this.selectedExpenseReports.length === 1) {
      this.applyBillingContextFromReport(expenseReport);
      this.loadCumulativesForSelectedProject();
    } else {
      this.refreshCategoryPreview();
    }
  }

  isExpenseReportSelected(expenseReport) {
    return this.selectedExpenseReports.some((report) => report.id === expenseReport.id);
  }

  isReportSelectable(expenseReport) {
    if (!this.selectedExpenseReports.length || !this.invoice.projectId) {
      return true;
    }
    return expenseReport.projectId === this.invoice.projectId;
  }

  getSelectedTotal() {
    return this.selectedExpenseReports.reduce((total, report) => {
      return total + (report.billableActualTotal || 0);
    }, 0);
  }

  refreshCategoryPreview() {
    const totals = {};

    this.selectedExpenseReports.forEach((report) => {
      (report.categoryAmounts || []).forEach((row) => {
        const category = row.category || 'Other';
        totals[category] = (totals[category] || 0) + (row.amount || 0);
      });
    });

    this.categoryPreview = Object.keys(totals).sort((a, b) => a.localeCompare(b)).map((category) => {
      const current = Math.round((totals[category] || 0) * 100) / 100;
      const prior = this.priorCumulatives[category] || 0;
      return {
        category,
        currentCost: current,
        cumulativeCost: Math.round((prior + current) * 100) / 100
      };
    });
  }

  getPreviewTotal() {
    return this.categoryPreview.reduce((total, row) => total + (row.currentCost || 0), 0);
  }

  getPreviewCumulativeTotal() {
    return this.categoryPreview.reduce((total, row) => total + (row.cumulativeCost || 0), 0);
  }

  getExpensesForCategory(category) {
    const target = (category || 'Other').trim().toLowerCase();
    const expenses = [];

    this.selectedExpenseReports.forEach((report) => {
      (report.billableExpenses || []).forEach((expense) => {
        const expenseCategory = (expense.type && String(expense.type).trim()) || 'Other';
        if (expenseCategory.toLowerCase() !== target) {
          return;
        }
        expenses.push(Object.assign({}, expense, {
          expenseReportId: expense.expenseReportId || report.id,
          reportName: report.name
        }));
      });
    });

    return expenses;
  }

  openCategoryCostModal(row) {
    if (!row) {
      return;
    }

    const apiHost = this.$rootScope.apiHost;
    const category = row.category || 'Expense Category';
    const expenses = this.getExpensesForCategory(category);

    this.$uibModal.open({
      template: `
        <div class="inmodal">
          <div class="modal-header">
            <h4 class="modal-title">Current Cost — {{$ctrl.category}}</h4>
          </div>
          <div class="modal-body">
            <p class="text-muted" ng-if="!$ctrl.expenses.length">
              No supporting expenses were found for this category in the selected reports.
            </p>
            <div ng-repeat="expense in $ctrl.expenses" class="m-b-md" style="border-bottom: 1px solid #eee; padding-bottom: 12px;">
              <div>
                <strong>{{expense.type || $ctrl.category}}</strong>
                — {{expense.description || 'No description'}}
              </div>
              <div class="text-muted small" ng-if="expense.reportName">
                Report: {{expense.reportName}}
              </div>
              <div class="text-muted small">
                Amount: {{$ctrl.formatCurrency(expense.actualAmount)}}
                <span ng-if="expense.fromDate"> · {{expense.fromDate | date:'MMM dd, yyyy'}}</span>
              </div>
              <div class="m-t-xs" ng-if="expense.documentationLink">
                <a ng-href="{{expense.documentationLink}}" target="_blank" rel="noopener noreferrer">Documentation link</a>
              </div>
              <div class="m-t-xs" ng-if="expense.receipts && expense.receipts.length">
                <div ng-repeat="receipt in expense.receipts">
                  <a ng-href="{{$ctrl.receiptUrl(expense, receipt)}}" target="_blank" rel="noopener noreferrer">
                    {{receipt.documentName || receipt.receipt || 'Receipt'}}
                  </a>
                </div>
              </div>
              <div class="text-muted small m-t-xs"
                   ng-if="(!expense.receipts || !expense.receipts.length) && !expense.documentationLink">
                No receipts attached.
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-default" ng-click="$ctrl.close()">Close</button>
          </div>
        </div>
      `,
      controller: ['$uibModalInstance', function ($uibModalInstance) {
        this.category = category;
        this.expenses = expenses;
        this.formatCurrency = (value) => formatCurrency(value || 0);
        this.receiptUrl = (expense, receipt) => {
          const reportId = expense.expenseReportId;
          const receiptKey = receipt.receipt;
          return `${apiHost}/api/expense-reports/${reportId}/expenses/${expense.id}/receipts/${receiptKey}`;
        };
        this.close = () => $uibModalInstance.dismiss('cancel');
      }],
      controllerAs: '$ctrl'
    });
  }

  getStatusLabel(status) {
    const details = this.travelExpenseService.getTravelExpenseStatusDetails(status);
    return details ? details.label : status;
  }

  canProceedToDetails() {
    return this.selectedExpenseReports.length > 0
      && !!this.invoice.customerId
      && !!this.invoice.contractId
      && !!this.invoice.projectId;
  }

  goToStep(step) {
    if (step === 1) {
      this.step = 1;
      return;
    }

    if (step === 2 && this.canProceedToDetails()) {
      this.nextStep();
    }
  }

  nextStep() {
    if (!this.canProceedToDetails()) {
      this.toastr.warning('Select one or more travel expense reports for the same project.', 'Incomplete');
      return;
    }

    this.refreshCategoryPreview();
    if (this.invoice.projectId && !Object.keys(this.priorCumulatives).length) {
      this.loadCumulativesForSelectedProject();
    }

    this.loadPreviewContext().then(() => {
      this.step = 2;
    });
  }

  previousStep() {
    this.step = 1;
  }

  canCreateInvoice() {
    return this.canProceedToDetails()
      && !this.creating
      && !!this.invoice.invoiceDate
      && !!this.invoice.invoicePeriodStart
      && !!this.invoice.invoicePeriodEnd;
  }

  createInvoice() {
    if (!this.canCreateInvoice()) {
      this.toastr.warning('Select one or more travel expense reports for the same project.', 'Incomplete');
      return;
    }

    this.creating = true;
    const label = String(this.invoice.invoiceNumberLabel || '').trim();
    const invoiceNumber = this.parseInvoiceNumberFromLabel(label);
    const payload = {
      invoiceNumber: invoiceNumber,
      invoiceNumberLabel: label || null,
      invoiceDate: this.moment(this.invoice.invoiceDate).format('YYYY-MM-DD'),
      invoicePeriodStart: this.moment(this.invoice.invoicePeriodStart).format('YYYY-MM-DD'),
      invoicePeriodEnd: this.moment(this.invoice.invoicePeriodEnd).format('YYYY-MM-DD'),
      signedDate: this.moment().format('YYYY-MM-DD'),
      certification: this.invoice.certification || null,
      customer: { id: this.invoice.customerId },
      contract: { id: this.invoice.contractId },
      project: { id: this.invoice.projectId },
      expenseReportIds: this.selectedExpenseReports.map((report) => report.id)
    };

    this.invoiceService.createTravelInvoice(payload).then((success) => {
      this.toastr.success('Travel invoice created.', 'Success');
      this.$state.go('admin.invoice-details', { invoiceId: success.data.id });
    }, (error) => {
      const message = (error.data && (error.data.message || error.data.error))
        || 'Could not create the travel invoice.';
      this.toastr.error(message, 'Error');
    }).finally(() => {
      this.creating = false;
    });
  }

  goBack() {
    this.$state.go('admin.invoice-create-wizard');
  }
}

export default InvoiceCreateTravelController;



// WEBPACK FOOTER //
// ./client/pages/admin/invoice-create-travel/invoice-create-travel.controller.js